/**
 * CommunitiesCatalogPage — the Discover Communities catalog (DEV-1447).
 *
 * The browse surface the follow-3 rung basic has been missing: until now the
 * only way to find a community was GlobalSearch, which requires already knowing
 * what you're looking for. The getting-started checklist's `follow_three` row
 * has pointed at `/discover` since it shipped; this is the surface it meant.
 *
 * DESIGN — hub-and-spoke (Skip, 2026-07-12; see
 * STATUS_DESIGN_2026-07-12_DISCOVER_CATALOGS_PHASE1.md §7.1):
 *   • The globe at /discover is the HUB. The PillarBar glyphs are the SPOKES.
 *   • This catalog is a LEAF: it carries NO pillar navigation of its own. To
 *     reach a different catalog the user returns to the globe (browser Back, or
 *     the LeftNav Discover item). A pillar rail here would be a second copy of
 *     the PillarBar, and would also be Discover's THIRD left column, which
 *     POLICY_NAVIGATION_RAILS §3 forbids.
 *   • It is a ROUTE (/discover/communities), not a state toggle inside
 *     /discover — browser Back only returns to the globe if the catalog owns a
 *     URL. Deep-linkability falls out for free.
 *
 * SCOPE — geo-only (§7.4). Organization communities are excluded; they arrive
 * with the Organizations catalog (the Enabler-pillar surface).
 *
 * SHELVES RECOMMEND, THE GRID INDEXES (Skip, 2026-07-13). The two surfaces have
 * different jobs, and that difference decides how a community the user ALREADY
 * FOLLOWS is treated:
 *   • Shelves are a recommendation surface — their scarce resource is the few
 *     slots a user actually looks at. Spending one on a community they have
 *     already followed is spending it on work already done. So followed
 *     communities are EXCLUDED from every shelf.
 *   • The grid is the complete index — hiding followed communities there would
 *     make it lie, and would leave no way to find one you follow. So they stay,
 *     with a "Not following" filter for when the user is specifically hunting
 *     for something new.
 */

import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import { useApiMyFollows } from '../../api/useApiMyFollows';
import CommunityCard from '../../Components/CommunityCard/CommunityCard';
import CatalogShelf from './CatalogShelf';
import {
  buildEntityMap,
  buildParentMap,
  ancestorGuids,
  type CommunityEntity,
  type CommunityRelationship,
} from '../../Components/CommunityCard/communityCardData';
import {
  isCatalogEligible,
  geoTier,
  joinsInWindow,
  isTooNewForGrowth,
  type GeoTier,
} from './catalogScope';
import { buildGeoTree, findNode, pathToNode, subtreeGuids } from './geoTree';
import styles from './CommunitiesCatalogPage.module.css';

interface Props {
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
  currentUserGuid?: string;
}

/** A shelf hides below this many items — the one-item-rail principle. */
const MIN_SHELF_ITEMS = 2;
/** Cards per shelf. A shelf is a curated slice, not a second grid. */
const SHELF_SIZE = 12;
/** The lead shelf trades quantity for presence — a few large cards, not a row. */
const LEAD_SHELF_SIZE = 4;

/** Which shelf's full list the grid is currently showing. */
type Slice = 'all' | 'nearYou' | 'mostMembers' | 'growing' | 'newlyAdded';

const CommunitiesCatalogPage: React.FC<Props> = ({
  entities,
  relationships,
  currentUserGuid,
}) => {
  const { t } = useAppTranslation('discover');
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const gridRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [tier, setTier] = useState<GeoTier>('all');
  const [slice, setSlice] = useState<Slice>('all');
  const [hideFollowed, setHideFollowed] = useState(false);

  const upperUser = (currentUserGuid || '').toUpperCase();

  /** The geo drilldown selection (slice 2). Lives in the URL, not in state:
   *  the GeoTreeRail is a SIBLING of this page in the shell, not a parent, so
   *  the URL is the only channel they share — and it makes a drilled-in view
   *  deep-linkable and Back-navigable for free. */
  const selectedGeo = (params.get('geo') || '').toUpperCase();

  const { communityMemberships } = useApiSecurityData();
  const { follows } = useApiMyFollows();

  const entityMap = useMemo(() => buildEntityMap(entities), [entities]);
  const parentMap = useMemo(() => buildParentMap(relationships), [relationships]);

  /**
   * The catalog's answer set — scoping enforced in the QUERY, not the render.
   * The predicate (geo-only, active, public, no workshops, no L4E sentinel)
   * lives in ./catalogScope.ts, where it is unit-tested: every exclusion is a
   * policy decision, not a styling choice.
   */
  const allEligible = useMemo(() => (entities || []).filter(isCatalogEligible), [entities]);

  /** The geo tree — shared with GeoTreeRail (both derive it; it is cheap and
   *  pure, and sharing an instance across sibling routes would need a context
   *  we do not otherwise want). */
  const geoRoots = useMemo(() => {
    const countable = new Set(allEligible.map((e) => e.entity_guid.toUpperCase()));
    return buildGeoTree(entities, parentMap, countable);
  }, [entities, parentMap, allEligible]);

  /** Breadcrumb for the current drilldown — includes inactive path segments. */
  const breadcrumb = useMemo(
    () => (selectedGeo ? pathToNode(geoRoots, selectedGeo) : []),
    [geoRoots, selectedGeo],
  );

  /**
   * The drilldown scopes the WHOLE catalog — shelves AND grid. One mental
   * model: the tree sets *where you are*, and everything below reflects it.
   * (Scoping only the grid would leave shelves recommending Nairobi while the
   * user is looking at New Hampshire.)
   */
  const catalog = useMemo(() => {
    if (!selectedGeo) return allEligible;
    const node = findNode(geoRoots, selectedGeo);
    if (!node) return allEligible; // stale/unknown ?geo= → degrade to unscoped
    const within = subtreeGuids(node);
    return allEligible.filter((c) => within.has(c.entity_guid.toUpperCase()));
  }, [allEligible, geoRoots, selectedGeo]);

  const setGeo = (guid: string | null) => {
    const next = new URLSearchParams(params);
    if (guid) next.set('geo', guid);
    else next.delete('geo');
    navigate({ pathname: location.pathname, search: next.toString() });
  };

  /** community_guid (upper) → active member count. Computed client-side; no
   *  aggregate endpoint exists (precedent: CommunityHealthTab). Correct at
   *  pilot scale; needs a server-side aggregate before this holds thousands. */
  const memberCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of communityMemberships || []) {
      const status = String((m as any).status || 'active').toLowerCase();
      if (status !== 'active') continue;
      const guid = String((m as any).community_entity_guid || '').toUpperCase();
      if (!guid) continue;
      counts.set(guid, (counts.get(guid) || 0) + 1);
    }
    return counts;
  }, [communityMemberships]);

  /** Joins in the trailing window — the "Growing" signal. See catalogScope.ts
   *  for why this is a windowed COUNT and not a MAX(join date). */
  const growthCounts = useMemo(
    () => joinsInWindow(communityMemberships as any),
    [communityMemberships],
  );

  /** The user's own member communities — the anchor for "Near you". */
  const myCommunityGuids = useMemo(() => {
    const set = new Set<string>();
    if (!upperUser) return set;
    for (const m of communityMemberships || []) {
      if (String((m as any).user_guid || '').toUpperCase() !== upperUser) continue;
      if (String((m as any).status || 'active').toLowerCase() !== 'active') continue;
      set.add(String((m as any).community_entity_guid || '').toUpperCase());
    }
    return set;
  }, [upperUser, communityMemberships]);

  /** Communities the user already follows. Two jobs:
   *   1. seeds each card's FollowButton (so the grid does not fire one status
   *      request per card on mount), and
   *   2. is excluded from the shelves — see the header note. */
  const followedGuids = useMemo(() => {
    const set = new Set<string>();
    for (const f of follows || []) {
      if (String(f.targetType).toLowerCase() !== 'community') continue;
      set.add(String(f.targetGuid).toUpperCase());
    }
    return set;
  }, [follows]);

  const byMembers = useMemo(
    () => (a: CommunityEntity, b: CommunityEntity) =>
      (memberCounts.get(b.entity_guid.toUpperCase()) || 0) -
      (memberCounts.get(a.entity_guid.toUpperCase()) || 0),
    [memberCounts],
  );

  /** Shelves recommend → they never show what the user already follows. */
  const unfollowed = useMemo(
    () => catalog.filter((c) => !followedGuids.has(c.entity_guid.toUpperCase())),
    [catalog, followedGuids],
  );

  // ── Slice: Near you ──
  // Ancestors and descendants of the communities the user belongs to. A user in
  // Hampton sees New Hampshire and the USA above, and other Hampton-adjacent
  // places below.
  const nearYouAll = useMemo(() => {
    if (myCommunityGuids.size === 0) return [];
    const near = new Set<string>();

    for (const guid of myCommunityGuids) {
      near.add(guid);
      for (const a of ancestorGuids(guid, parentMap)) near.add(a);
    }
    for (const c of unfollowed) {
      const chain = ancestorGuids(c.entity_guid, parentMap);
      if (chain.some((a) => myCommunityGuids.has(a))) near.add(c.entity_guid.toUpperCase());
    }

    return unfollowed
      .filter((c) => near.has(c.entity_guid.toUpperCase()))
      .sort(byMembers);
  }, [unfollowed, myCommunityGuids, parentMap, byMembers]);

  // ── Slice: Most members ──
  const mostMembersAll = useMemo(
    () =>
      [...unfollowed]
        .filter((c) => (memberCounts.get(c.entity_guid.toUpperCase()) || 0) > 0)
        .sort(byMembers),
    [unfollowed, memberCounts, byMembers],
  );

  // ── Slice: Growing ──
  // Joins in the trailing window. NOT "recently active" — there is no
  // timestamped, geo-attributable activity signal on a community today
  // (activity_events, migration 241, is the purpose-built table and is
  // effectively empty). The shelf is labelled for what it actually measures.
  // Communities younger than the window are excluded: all of their joins fall
  // inside it by definition, so they would win on newness alone — and they are
  // already on "Newly added".
  const growingAll = useMemo(
    () =>
      unfollowed
        .filter((c) => !isTooNewForGrowth(c))
        .map((c) => ({ c, n: growthCounts.get(c.entity_guid.toUpperCase()) || 0 }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n || byMembers(a.c, b.c))
        .map((x) => x.c),
    [unfollowed, growthCounts, byMembers],
  );

  // ── Slice: Newly added ──
  const newlyAddedAll = useMemo(
    () =>
      [...unfollowed]
        .filter((c) => !!c.created_at)
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
    [unfollowed],
  );

  const sliceLists = useMemo<Record<Exclude<Slice, 'all'>, CommunityEntity[]>>(
    () => ({
      nearYou: nearYouAll,
      mostMembers: mostMembersAll,
      growing: growingAll,
      newlyAdded: newlyAddedAll,
    }),
    [nearYouAll, mostMembersAll, growingAll, newlyAddedAll],
  );

  // ── Browse-all grid ──
  // The grid INDEXES: followed communities stay (removing them would make the
  // complete list incomplete), with an opt-in "Not following" filter for when
  // the user is specifically hunting for something new.
  const gridItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = slice === 'all' ? catalog : sliceLists[slice];
    return base
      .filter((c) => (tier === 'all' ? true : geoTier(c.entity_type) === tier))
      .filter((c) => (hideFollowed ? !followedGuids.has(c.entity_guid.toUpperCase()) : true))
      .filter((c) => (q ? String(c.name || '').toLowerCase().includes(q) : true))
      .sort(
        (a, b) =>
          // A slice carries its own ordering; "All" sorts by size, then name.
          (slice === 'all' ? byMembers(a, b) : 0) ||
          (slice === 'all' ? String(a.name || '').localeCompare(String(b.name || '')) : 0),
      );
  }, [catalog, sliceLists, slice, tier, query, hideFollowed, followedGuids, byMembers]);

  const openCommunity = (guid: string) => navigate(`/communities/${guid}`);

  const showSlice = (s: Slice) => {
    setSlice(s);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * NOTE: never call as `list.map(renderCard)` — Array.map passes the INDEX as
   * the second argument, which would land in `size`. Always wrap:
   * `list.map((c) => renderCard(c))`.
   */
  const renderCard = (c: CommunityEntity, size: 'default' | 'large' = 'default') => (
    <CommunityCard
      key={c.entity_guid}
      entity={c}
      entityMap={entityMap}
      parentMap={parentMap}
      onOpen={openCommunity}
      memberCount={memberCounts.get(c.entity_guid.toUpperCase()) || 0}
      initialFollowing={followedGuids.has(c.entity_guid.toUpperCase())}
      size={size}
    />
  );

  /**
   * Shelf order. The FIRST shelf that actually renders becomes the LEAD and gets
   * large cards — the rule is positional, not per-shelf, so nothing is
   * special-cased and the treatment survives an empty shelf hiding and the lead
   * position shifting down. (When Featured ships it slots in at the head of this
   * array and inherits the lead treatment automatically.)
   *
   * Empty-shelf discipline: a shelf with fewer than MIN_SHELF_ITEMS hides
   * entirely rather than rendering as a near-empty row. At pilot scale the
   * catalog must read as EARLY, not BROKEN.
   */
  const SHELF_ORDER: Exclude<Slice, 'all'>[] = ['nearYou', 'mostMembers', 'growing', 'newlyAdded'];
  const visibleShelves = SHELF_ORDER.filter((k) => sliceLists[k].length >= MIN_SHELF_ITEMS);

  const renderShelf = (key: Exclude<Slice, 'all'>, isLead: boolean) => {
    const all = sliceLists[key];
    // The lead shelf shows fewer, larger cards — a focal point, not a second grid.
    const take = isLead ? LEAD_SHELF_SIZE : SHELF_SIZE;
    return (
      <CatalogShelf
        key={key}
        title={t(`catalog.communities.shelf.${key}`)}
        totalCount={all.length}
        lead={isLead}
        onSeeAll={all.length > take ? () => showSlice(key) : undefined}
      >
        {all.slice(0, take).map((c) => renderCard(c, isLead ? 'large' : 'default'))}
      </CatalogShelf>
    );
  };

  const tiers: GeoTier[] = ['all', 'country', 'region', 'local'];

  return (
    /* NOT `pageContainer` (Skip, 2026-07-13). The shared container caps at
       1200px to protect line length — a rule for PROSE. This is a card grid,
       where the cap costs cards-per-row and makes shelves overflow sooner than
       they need to. The catalog uses a wider cap of its own (see .page).
       Grammar note: this is a deliberate exception to the app-wide page shell,
       justified because Discover is ALREADY full-bleed (the globe fills the
       viewport) — so a wide catalog is consistent with the SECTION it lives in,
       even though it diverges from the standard page. */
    <div className={styles.page}>
      <header className={styles.header}>
        <button type="button" className={styles.back} onClick={() => navigate('/discover')}>
          {t('catalog.backToGlobe')}
        </button>
        <h1 className={styles.title}>{t('catalog.communities.title')}</h1>
        <input
          className={styles.search}
          placeholder={t('catalog.communities.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t('catalog.communities.searchPlaceholder')}
        />
      </header>

      {/* Drilldown breadcrumb. Every segment is a jump target — including the
          inactive ones, which scope the catalog even though they are not
          joinable communities themselves (§7.5). */}
      {breadcrumb.length > 0 && (
        <nav className={styles.breadcrumb} aria-label={t('catalog.tree.breadcrumbLabel')}>
          <button type="button" className={styles.crumb} onClick={() => setGeo(null)}>
            {t('catalog.tree.everywhere')}
          </button>
          {breadcrumb.map((node, i) => {
            const isLast = i === breadcrumb.length - 1;
            return (
              <React.Fragment key={node.guid}>
                <span className={styles.crumbSep} aria-hidden="true">/</span>
                <button
                  type="button"
                  className={`${styles.crumb} ${isLast ? styles.crumbCurrent : ''} ${
                    node.inactive ? styles.crumbInactive : ''
                  }`}
                  onClick={() => setGeo(node.guid)}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {node.name}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {/* ── Shelves — recommend. Never show what the user already follows.
             The first one to render leads, and leads large. ── */}
      {visibleShelves.map((key, i) => renderShelf(key, i === 0))}

      {/* ── Browse all — indexes. Complete, followed included. ── */}
      <section className={styles.gridSection} ref={gridRef}>
        <div className={styles.gridHeader}>
          <h2 className={styles.gridTitle}>{t('catalog.communities.browseAll')}</h2>

          {/* An applied slice is a removable filter, not a hidden mode. */}
          {slice !== 'all' && (
            <button
              type="button"
              className={styles.sliceChip}
              onClick={() => setSlice('all')}
            >
              {t(`catalog.communities.shelf.${slice}`)}
              <span aria-hidden="true" className={styles.sliceChipX}>×</span>
              <span className={styles.srOnly}>{t('catalog.clearSlice')}</span>
            </button>
          )}

          <div className={styles.chips} role="group" aria-label={t('catalog.communities.filterLabel')}>
            {tiers.map((tr) => (
              <button
                key={tr}
                type="button"
                className={`${styles.chip} ${tier === tr ? styles.chipOn : ''}`}
                onClick={() => setTier(tr)}
                aria-pressed={tier === tr}
              >
                {t(`catalog.communities.tier.${tr}`)}
              </button>
            ))}

            <button
              type="button"
              className={`${styles.chip} ${hideFollowed ? styles.chipOn : ''}`}
              onClick={() => setHideFollowed((v) => !v)}
              aria-pressed={hideFollowed}
            >
              {t('catalog.communities.notFollowing')}
            </button>
          </div>

          <span className={styles.count}>{gridItems.length}</span>
        </div>

        {gridItems.length === 0 ? (
          <div className={styles.empty}>
            {query
              ? t('catalog.communities.noMatch', { query })
              : t('catalog.communities.empty')}
          </div>
        ) : (
          <div className={styles.grid}>{gridItems.map((c) => renderCard(c))}</div>
        )}
      </section>
    </div>
  );
};

export default CommunitiesCatalogPage;
